import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Send, Trash2, KeyRound } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PatientFields, cleanPatient } from "@/components/patient/PatientFields";

const AccessCard = ({ p, reload }) => {
  const [sending, setSending] = useState(false);
  const invite = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/patients/${p.id}/invite`);
      toast[data.email_sent ? "success" : "warning"](data.email_sent ? "Email di accesso inviata" : "Account creato, ma l'email non è stata inviata");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="card space-y-4" data-testid="patient-access-card">
      <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#3d655b]" /><h3 className="text-lg font-bold">Area personale</h3></div>
      {p.user_id ? (
        <p className="text-sm text-slate-600" data-testid="patient-access-status">Accesso creato{p.invited_at ? ` · invito inviato il ${fmtDateTime(p.invited_at)}` : ""}. Il paziente vede la sua scheda, le visite, le patologie, gli appuntamenti e il piano nutrizionale solo se reso visibile. <strong>Gli appunti privati non sono mai visibili.</strong></p>
      ) : (
        <p className="text-sm text-slate-600" data-testid="patient-access-status">Il paziente non ha ancora un accesso. {p.email ? "Invia l'invito per creare il suo account." : "Aggiungi un'email per poter inviare l'invito."}</p>
      )}
      <button className="btn-outline" disabled={!p.email || sending} onClick={invite} data-testid="patient-invite-button">
        <Send className="h-4 w-4" /> {p.user_id ? "Reinvia email di accesso" : "Invia accesso"}
      </button>
    </div>
  );
};

export function AnagraficaTab({ rec, reload }) {
  const p = rec.patient;
  const nav = useNavigate();
  const [form, setForm] = useState(p);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(p); }, [p]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, user_id, created_at, invited_at, ...rest } = form;
      await api.put(`/patients/${p.id}`, cleanPatient(rest));
      toast.success("Anagrafica salvata");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare definitivamente la scheda di ${p.first_name} ${p.last_name}? Tutti i dati verranno cancellati.`)) return;
    await api.delete(`/patients/${p.id}`);
    toast.success("Paziente eliminato");
    nav("/pazienti");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={save} className="card space-y-5 lg:col-span-2" data-testid="anagrafica-form">
        <h3 className="text-lg font-bold">Dati anagrafici</h3>
        <PatientFields form={form} setForm={setForm} />
        <div className="flex justify-end">
          <button className="btn-primary" disabled={saving} data-testid="anagrafica-save-button"><Save className="h-4 w-4" /> Salva</button>
        </div>
      </form>
      <div className="space-y-6">
        <AccessCard p={p} reload={reload} />
        <button onClick={remove} className="btn-ghost text-rose-600 hover:bg-rose-50" data-testid="patient-delete-button"><Trash2 className="h-4 w-4" /> Elimina paziente</button>
      </div>
    </div>
  );
}
